/**
 * Active Carts Page
 * 
 * Displays all active customer carts with filtering, sorting, and pagination.
 * Allows sales team to view cart details and take quick actions.
 */
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridSortModel
} from '@mui/x-data-grid';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  History as HistoryIcon,
  Phone as PhoneIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  WhatsApp as WhatsAppIcon,
  CheckCircle as CheckIcon,
  FlashOn as FlashIcon,
  Sms as SmsIcon,
  Send as SendIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Divider,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { CartService } from '../services/cartService';
import { ActiveCart, CartFilters } from '../types/activeCart';
import CartDetailModal from '../components/carts/CartDetailModal';
import { formatCurrency } from '../utils/currencyFormatters';

const ActiveCartsPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  
  // Filter state
  const [filters, setFilters] = useState<CartFilters>({
    page: 1,
    page_size: 20,
    sort_by: 'value',
    sort_order: 'desc'
  });

  // Modal state
  const [selectedCartId, setSelectedCartId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // WhatsApp Dialog state
  const [waDialogOpen, setWaDialogOpen] = useState(false);
  const [waLoading, setWaLoading] = useState(false);
  const [waPreview, setWaPreview] = useState<{
    customer_name: string;
    phone: string;
    total_value: number;
    message: string;
    whatsapp_url: string | null;
  } | null>(null);

  // SMS Dialog state
  const [smsDialogOpen, setSmsDialogOpen] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsTargetCart, setSmsTargetCart] = useState<ActiveCart | null>(null);
  const [smsPhone, setSmsPhone] = useState('');
  const [smsMessage, setSmsMessage] = useState('');

  // Fetch summary stats
  const { data: summaryStats, refetch: refetchStats } = useQuery({
    queryKey: ['active-carts-summary'],
    queryFn: () => CartService.getSummaryStats(),
    staleTime: 60 * 1000,
  });

  // Fetch active carts
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['active-carts', filters],
    queryFn: () => CartService.getActiveCarts(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Handle filter changes
  const handleFilterChange = (field: keyof CartFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: field !== 'page' ? 1 : value // Reset to page 1 when filters change
    }));
  };

  // Handle sort changes
  const handleSortModelChange = (model: GridSortModel) => {
    if (model.length > 0) {
      const { field, sort } = model[0];
      setFilters(prev => ({
        ...prev,
        sort_by: field as 'value' | 'age' | 'last_activity',
        sort_order: sort as 'asc' | 'desc'
      }));
    }
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    handleFilterChange('page', newPage + 1); // MUI DataGrid uses 0-based indexing
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setFilters(prev => ({
      ...prev,
      page_size: newPageSize,
      page: 1
    }));
  };

  // Handle cart detail view
  const handleViewCart = (cartId: string) => {
    setSelectedCartId(cartId);
    setModalOpen(true);
  };

  // Handle quick actions
  const handleContactCustomer = (phone: string) => {
    if (phone) {
      window.location.href = `tel:${phone}`;
    }
  };

  const handleViewCustomerProfile = (customerId: string) => {
    if (customerId) {
      navigate(`/admin/parties/ledgers?customerId=${customerId}`);
    } else {
      navigate('/admin/parties/ledgers');
    }
  };

  const handleViewOrderHistory = (customerId: string) => {
    if (customerId) {
      navigate(`/admin/documents/sales-orders?customer_id=${customerId}`);
    } else {
      navigate('/admin/documents/sales-orders');
    }
  };

  const handleWhatsAppRecovery = async (cartId: string) => {
    setWaLoading(true);
    try {
      const preview = await CartService.getWhatsAppPreview(cartId);
      setWaPreview(preview);
      setWaDialogOpen(true);
    } catch (err) {
      console.error('Failed to get WhatsApp preview:', err);
    } finally {
      setWaLoading(false);
    }
  };

  const handleOpenSmsDialog = (cart: ActiveCart) => {
    const phone = cart.customer_phone || (cart as any).customer_mobile || '';
    const formattedTotal = Number(cart.total_value || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    setSmsTargetCart(cart);
    setSmsPhone(phone);
    setSmsMessage(
      `Greetings from Dhanshika.in! Namaste ${cart.customer_name} ji, your wholesale cart with ${cart.item_count} items worth Rs. ${formattedTotal} is waiting. Please let us know if you have any questions or complete order at https://dhanshika.in/cart - Dhanshika.in`
    );
    setSmsDialogOpen(true);
  };

  const handleSendSms = async () => {
    if (!smsTargetCart) return;
    try {
      setSmsLoading(true);
      const res = await CartService.sendSMS(smsTargetCart.cart_id, smsMessage, smsPhone);
      enqueueSnackbar(`SMS sent successfully to ${res.recipient || smsPhone}!`, { variant: 'success' });
      setSmsDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to send SMS:', err);
      enqueueSnackbar(err?.response?.data?.detail || 'Failed to send SMS', { variant: 'error' });
    } finally {
      setSmsLoading(false);
    }
  };

  const handleExportActiveCarts = async () => {
    try {
      const blob = await CartService.exportActiveCarts(filters);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `active_carts_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting active carts:', error);
    }
  };

  // Calculate cart age in days for display
  const getCartAgeDays = (ageHours: number): number => {
    return Math.floor(ageHours / 24);
  };

  // Check if cart is stale (>7 days)
  const isStaleCart = (ageHours: number): boolean => {
    return ageHours > 168; // 7 days = 168 hours
  };

  // Format last activity timestamp
  const formatLastActivity = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) {
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes} min ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hours ago`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} days ago`;
    }
  };

  // Define columns
  const columns: GridColDef[] = [
    {
      field: 'customer_name',
      headerName: 'Customer',
      flex: 1,
      minWidth: 180,
      renderCell: (params: GridRenderCellParams<ActiveCart>) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.row.customer_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.customer_type}
          </Typography>
        </Box>
      )
    },
    {
      field: 'customer_phone',
      headerName: 'Phone / Contact',
      width: 170,
      renderCell: (params: GridRenderCellParams<ActiveCart>) => {
        const phone = params.row.customer_phone || (params.row as any).customer_mobile;
        if (!phone) {
          return <Typography variant="caption" color="text.disabled">No Phone</Typography>;
        }
        return (
          <Box display="flex" flexDirection="column">
            <Typography variant="body2" fontWeight={600} color="text.primary">
              {phone}
            </Typography>
            <Stack direction="row" spacing={0.5} mt={0.25}>
              <Tooltip title="Copy Phone Number">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigator.clipboard.writeText(phone);
                    enqueueSnackbar(`Copied ${phone} to clipboard!`, { variant: 'info', autoHideDuration: 2000 });
                  }}
                  sx={{ p: 0.25 }}
                >
                  <CopyIcon sx={{ fontSize: 14 }} color="action" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Call">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    window.location.href = `tel:${phone}`;
                  }}
                  sx={{ p: 0.25 }}
                >
                  <PhoneIcon sx={{ fontSize: 14 }} color="primary" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Send WhatsApp">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleWhatsAppRecovery(params.row.cart_id);
                  }}
                  sx={{ p: 0.25 }}
                >
                  <WhatsAppIcon sx={{ fontSize: 14, color: '#25D366' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Send SMS">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenSmsDialog(params.row);
                  }}
                  sx={{ p: 0.25 }}
                >
                  <SmsIcon sx={{ fontSize: 14, color: '#0288D1' }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        );
      }
    },
    {
      field: 'item_count',
      headerName: 'Items',
      width: 90,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'total_value',
      headerName: 'Cart Value',
      width: 140,
      renderCell: (params: GridRenderCellParams<ActiveCart>) => (
        <Typography variant="body2" fontWeight="bold" color="primary.main">
          {formatCurrency(params.row.total_value)}
        </Typography>
      )
    },
    {
      field: 'cart_age_hours',
      headerName: 'Cohort & Age',
      width: 140,
      renderCell: (params: GridRenderCellParams<ActiveCart>) => {
        const hours = params.row.cart_age_hours || 0;
        const isHot = hours <= 24;
        const isWarm = hours > 24 && hours <= 72;
        const isStale = hours > 72;
        const ageDays = getCartAgeDays(hours);
        
        return (
          <Box display="flex" alignItems="center" gap={0.8}>
            <Chip
              label={isHot ? '🔥 Hot' : isWarm ? '🟡 Warm' : '🔴 Stale'}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.68rem',
                fontWeight: 700,
                bgcolor: isHot ? '#DCFCE7' : isWarm ? '#FEF3C7' : '#FEE2E2',
                color: isHot ? '#15803D' : isWarm ? '#B45309' : '#B91C1C',
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {ageDays === 0 ? `${hours}h` : `${ageDays}d`}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'last_activity',
      headerName: 'Last Activity',
      width: 130,
      renderCell: (params: GridRenderCellParams<ActiveCart>) => (
        <Typography variant="body2" color="text.secondary">
          {formatLastActivity(params.row.last_activity)}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Quick Recovery',
      width: 230,
      sortable: false,
      renderCell: (params: GridRenderCellParams<ActiveCart>) => (
        <Box display="flex" gap={0.5} alignItems="center">
          <Tooltip title="Send 1-Click WhatsApp Recovery">
            <span>
              <IconButton
                size="small"
                onClick={() => handleWhatsAppRecovery(params.row.cart_id)}
                disabled={!params.row.customer_phone}
                sx={{
                  color: '#25D366',
                  bgcolor: 'rgba(37, 211, 102, 0.08)',
                  '&:hover': { bgcolor: 'rgba(37, 211, 102, 0.18)' },
                }}
              >
                <WhatsAppIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Send Direct SMS Recovery">
            <span>
              <IconButton
                size="small"
                onClick={() => handleOpenSmsDialog(params.row)}
                disabled={!params.row.customer_phone}
                sx={{
                  color: '#0288D1',
                  bgcolor: 'rgba(2, 136, 209, 0.08)',
                  '&:hover': { bgcolor: 'rgba(2, 136, 209, 0.18)' },
                }}
              >
                <SmsIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="View Cart Details">
            <IconButton
              size="small"
              onClick={() => handleViewCart(params.row.cart_id)}
              color="primary"
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Call Customer">
            <span>
              <IconButton
                size="small"
                onClick={() => handleContactCustomer(params.row.customer_phone || '')}
                disabled={!params.row.customer_phone}
                color="default"
              >
                <PhoneIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Customer Ledger">
            <IconButton
              size="small"
              onClick={() => handleViewCustomerProfile(params.row.customer_id)}
              color="default"
            >
              <PersonIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sales Orders / History">
            <IconButton
              size="small"
              onClick={() => handleViewOrderHistory(params.row.customer_id)}
              color="default"
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Active Carts & Recovery Hub
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Monitor customer carts, analyze stranded wholesale revenue, and take 1-click WhatsApp recovery action
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Tooltip title="Export active carts to CSV">
            <Button
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleExportActiveCarts}
              disabled={isLoading}
            >
              Export CSV
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              refetch();
              refetchStats();
            }}
            disabled={isLoading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Summary Stats & Recovery Badges */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #6366F1' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              TOTAL STRANDED VALUE
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="primary.main" mt={0.5}>
              {formatCurrency(summaryStats?.total_stranded_value ?? (data?.total_value || 0))}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              across {summaryStats?.total_active_carts ?? (data?.total || 0)} carts
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #10B981' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              🔥 HOT CARTS (&lt;24h)
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="success.main" mt={0.5}>
              {summaryStats?.hot_carts_count ?? 0}
            </Typography>
            <Typography variant="caption" color="success.dark">
              Highest recovery likelihood
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #F59E0B' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              🟡 WARM CARTS (24-72h)
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="warning.main" mt={0.5}>
              {summaryStats?.warm_carts_count ?? 0}
            </Typography>
            <Typography variant="caption" color="warning.dark">
              Needs follow-up incentive
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #EF4444' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              🔴 STALE CARTS (&gt;72h)
            </Typography>
            <Typography variant="h5" fontWeight="bold" color="error.main" mt={0.5}>
              {summaryStats?.stale_carts_count ?? (data?.stale_carts_count || 0)}
            </Typography>
            <Typography variant="caption" color="error.dark">
              High drop-off risk
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 2, borderRadius: 2, borderLeft: '4px solid #8B5CF6' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              👑 WHOLESALER CARTS (&gt;₹25k)
            </Typography>
            <Typography variant="h5" fontWeight="bold" sx={{ color: '#7C3AED' }} mt={0.5}>
              {summaryStats?.high_value_wholesaler_count ?? 0}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              High-margin VIP orders
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              label="Customer Type"
              value={filters.customer_type || ''}
              onChange={(e) => handleFilterChange('customer_type', e.target.value || undefined)}
              size="small"
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="retail">Retail</MenuItem>
              <MenuItem value="reseller">Reseller</MenuItem>
              <MenuItem value="wholesaler">Wholesaler</MenuItem>
              <MenuItem value="broker">Broker</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Min Value"
              value={filters.min_value || ''}
              onChange={(e) => handleFilterChange('min_value', e.target.value ? parseFloat(e.target.value) : undefined)}
              size="small"
              inputProps={{ min: 0, step: 100 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Max Value"
              value={filters.max_value || ''}
              onChange={(e) => handleFilterChange('max_value', e.target.value ? parseFloat(e.target.value) : undefined)}
              size="small"
              inputProps={{ min: 0, step: 100 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Min Age (hours)"
              value={filters.min_age_hours || ''}
              onChange={(e) => handleFilterChange('min_age_hours', e.target.value ? parseInt(e.target.value) : undefined)}
              size="small"
              inputProps={{ min: 0, step: 24 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Max Age (hours)"
              value={filters.max_age_hours || ''}
              onChange={(e) => handleFilterChange('max_age_hours', e.target.value ? parseInt(e.target.value) : undefined)}
              size="small"
              inputProps={{ min: 0, step: 24 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setFilters({ page: 1, page_size: 20, sort_by: 'value', sort_order: 'desc' })}
              size="small"
              sx={{ height: '40px' }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to load active carts. Please try again.
        </Alert>
      )}

      {/* Data Grid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={data?.carts || []}
          columns={columns}
          getRowId={(row) => row.cart_id}
          loading={isLoading}
          paginationMode="server"
          rowCount={data?.total || 0}
          paginationModel={{
            page: (filters.page || 1) - 1,
            pageSize: filters.page_size || 20
          }}
          onPaginationModelChange={(model) => {
            setFilters(prev => ({
              ...prev,
              page: model.page + 1,
              page_size: model.pageSize
            }));
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          sortingMode="server"
          sortModel={[
            {
              field: filters.sort_by || 'value',
              sort: filters.sort_order || 'desc'
            }
          ]}
          onSortModelChange={handleSortModelChange}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-row:hover': {
              cursor: 'pointer'
            }
          }}
          onRowClick={(params) => handleViewCart(params.row.cart_id)}
        />
      </Paper>

      {/* Cart Detail Modal */}
      {selectedCartId && (
        <CartDetailModal
          cartId={selectedCartId}
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedCartId(null);
          }}
        />
      )}

      {/* WhatsApp 1-Click Recovery Dialog */}
      <Dialog open={waDialogOpen} onClose={() => setWaDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#128C7E' }}>
          <WhatsAppIcon /> 1-Click WhatsApp Cart Recovery
        </DialogTitle>
        <DialogContent dividers>
          {waLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" p={3}>
              <CircularProgress size={30} />
            </Box>
          ) : waPreview ? (
            <Box>
              <Typography variant="body2" color="textSecondary" mb={1}>
                Customer: <strong>{waPreview.customer_name}</strong> ({waPreview.phone ? `+${waPreview.phone}` : 'No phone'})
              </Typography>
              <Typography variant="caption" color="primary.main" fontWeight={700} display="block" mb={1}>
                Cart Value: {formatCurrency(waPreview.total_value)}
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: '#F0FDF4', borderColor: '#BBF7D0', whiteSpace: 'pre-line' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                  {waPreview.message}
                </Typography>
              </Paper>
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No preview available.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setWaDialogOpen(false)} color="inherit">
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<WhatsAppIcon />}
            disabled={!waPreview?.whatsapp_url}
            onClick={() => {
              if (waPreview?.whatsapp_url) {
                window.open(waPreview.whatsapp_url, '_blank');
                setWaDialogOpen(false);
              }
            }}
            sx={{ bgcolor: '#25D366', color: '#fff', '&:hover': { bgcolor: '#128C7E' } }}
          >
            Launch WhatsApp Web / App
          </Button>
        </DialogActions>
      </Dialog>

      {/* Direct SMS Sending Dialog */}
      <Dialog open={smsDialogOpen} onClose={() => !smsLoading && setSmsDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'primary.main', fontWeight: 600 }}>
          <SmsIcon color="primary" /> Send Direct SMS Notification
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <Typography variant="body2" color="text.secondary">
              Send an instant SMS reminder to <strong>{smsTargetCart?.customer_name}</strong> regarding their reserved cart.
            </Typography>
            <TextField
              label="Recipient Mobile Number"
              fullWidth
              size="small"
              value={smsPhone}
              onChange={(e) => setSmsPhone(e.target.value)}
              placeholder="e.g. 9876543210"
              helperText="10-digit mobile number"
            />
            <TextField
              label="SMS Message Content"
              fullWidth
              multiline
              rows={4}
              value={smsMessage}
              onChange={(e) => setSmsMessage(e.target.value)}
              helperText={`${smsMessage.length} characters (1 credit / 160 chars)`}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSmsDialogOpen(false)} disabled={smsLoading} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            disabled={smsLoading || !smsPhone.trim() || !smsMessage.trim()}
            onClick={handleSendSms}
          >
            {smsLoading ? 'Sending SMS...' : 'Send SMS Now'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActiveCartsPage;
